import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Icons } from '@/src/modules/components/Icons';
import { WizardData } from '@/src/types';
import { StepOccasion } from './StepOccasion';
import { StepTemplate } from './StepTemplate';
import { StepDetails } from './StepDetails';
import { ExperienceDetailsStep } from './steps/ExperienceDetailsStep';
import { isAiNativeExperience, resolveEditEntryStep } from './wizard-routing';
import { StepPublish } from './StepPublish';
import { DraftSaveIndicator } from './DraftSaveIndicator';
import { BrandLogo } from '@/src/modules/components/BrandLogo';
import { useDraftAutosave } from './useDraftAutosave';
import { getDraft } from '@/src/infrastructure/lib/api';
import { cn } from '@/src/infrastructure/lib/utils';
import { todayInputValue, currentTimeInputValue } from '@/src/infrastructure/lib/date';
import { useWizardTiming } from '@/src/infrastructure/shared/analytics/useWizardTiming';

/**
 * Optional launch state, e.g. when the user clicks "Use this template" in the
 * Template Gallery: the occasion + template are pre-selected and the wizard
 * jumps straight to the Details step.
 */
interface WizardLaunchState extends Partial<WizardData> {
  initialStep?: number;
}

function parseEventDateToInputs(eventDate?: string | Date | null): { _dateInput?: string; _timeInput?: string } {
  if (!eventDate) return {};
  try {
    const d = new Date(eventDate);
    if (isNaN(d.getTime())) return {};
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return {
      _dateInput: `${year}-${month}-${day}`,
      _timeInput: `${hours}:${minutes}`,
    };
  } catch {
    return {};
  }
}

/** Reconstruct wizard state + step from a draft's `draftMeta` blob. */
function hydrateFromDraftMeta(
  draftMeta: Record<string, unknown> | null | undefined,
): { data: WizardData; step: number } | null {
  if (!draftMeta) return null;
  const { step, ...rest } = draftMeta as { step?: number } & Record<string, unknown>;
  return { data: rest as WizardData, step: typeof step === 'number' ? step : 3 };
}

export function Wizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const launch = (location.state ?? {}) as WizardLaunchState;
  const { initialStep, ...seedData } = launch;
  const [searchParams] = useSearchParams();

  // Resume an existing server-side draft when `?draftId=` is present — used by
  // the Drafts dashboard "Continue editing" deep-link, recovery prompt, and AI/MCP assistant.
  const resumeDraftId = searchParams.get('draftId');
  const token = searchParams.get('token');

  const [step, setStep] = useState(initialStep ?? 1);
  const [data, setData] = useState<WizardData>({
    _dateInput: todayInputValue(),
    _timeInput: currentTimeInputValue(),
    ...seedData,
  });
  const [hydrating, setHydrating] = useState<boolean>(Boolean(resumeDraftId));

  // Mirrors `data`, but updated synchronously so `goToStep` (below) can read
  // the just-applied change within the same event-handler tick. Every step's
  // "select and advance" onClick does `updateData({...}); onNext();`
  // synchronously — `setData`'s update doesn't land until the next render, so
  // `onNext`'s `goToStep` (a closure captured at render time, before this
  // click) was reading the PREVIOUS render's `maxReachableStep`, one step
  // behind the selection that just happened. That silently blocked the very
  // first click on every step (occasion, template, ...) — advancing only
  // worked on a second click, once a render had caught up. `dataRef` fixes
  // this by giving `goToStep` a value that's current the instant `updateData`
  // runs, not just as of the last render.
  const dataRef = useRef(data);

  const updateData = (newData: Partial<WizardData> | ((prev: WizardData) => Partial<WizardData>)) => {
    const patch = typeof newData === 'function' ? newData(dataRef.current) : newData;
    dataRef.current = { ...dataRef.current, ...patch };
    setData(dataRef.current);
  };

  // Hydrate from the server once on mount when resuming a draft. `draftMeta` is
  // merged with structured columns to ensure 100% complete field + step restore.
  useEffect(() => {
    if (!resumeDraftId) return;
    let cancelled = false;
    getDraft(resumeDraftId, token)
      .then((draft) => {
        if (cancelled) return;
        const fromMeta = hydrateFromDraftMeta(draft.draftMeta);
        const dateInputs = parseEventDateToInputs(draft.eventDate);

        const restoredData: WizardData = {
          _dateInput: todayInputValue(),
          _timeInput: currentTimeInputValue(),
          ...(fromMeta?.data ?? {}),
          templateId: draft.templateId || fromMeta?.data?.templateId || '',
          occasionType: draft.occasionType || fromMeta?.data?.occasionType || 'WEDDING',
          eventName: draft.eventName ?? fromMeta?.data?.eventName,
          hostName: draft.hostName ?? fromMeta?.data?.hostName,
          eventVenue: draft.eventVenue ?? fromMeta?.data?.eventVenue,
          customMessage: draft.customMessage ?? fromMeta?.data?.customMessage,
          rsvpLink: draft.rsvpLink ?? fromMeta?.data?.rsvpLink,
          mediaUrls: draft.mediaUrls ?? fromMeta?.data?.mediaUrls ?? [],
          customData: (draft.customData as Record<string, unknown> | null) ?? fromMeta?.data?.customData ?? undefined,
          ...dateInputs,
          ...(fromMeta?.data?._dateInput ? { _dateInput: fromMeta.data._dateInput } : {}),
          ...(fromMeta?.data?._timeInput ? { _timeInput: fromMeta.data._timeInput } : {}),
        };

        const targetStep = resolveEditEntryStep(Boolean(draft.templateId), fromMeta?.step);
        // Keep dataRef in lockstep — see its declaration for why goToStep
        // depends on it, not on render-time `data`.
        dataRef.current = restoredData;
        setData(restoredData);
        setStep(targetStep);
      })
      .catch(() => {
        // A missing/forbidden draft just starts a fresh wizard.
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeDraftId, token]);

  const { draftId, status: saveStatus, saveNow } = useDraftAutosave({
    data,
    step,
    initialDraftId: resumeDraftId,
    token,
    enabled: !hydrating,
  });

  // Which Details step to mount: the DSL builder, or the generic form? See
  // wizard-routing.ts for why this is a separate, tested function rather than
  // inline logic — it was silently narrowed to wedding-only once already.
  const DetailsComponent = isAiNativeExperience(data) ? ExperienceDetailsStep : StepDetails;

  const steps = [
    { title: 'Occasion', component: StepOccasion },
    { title: 'Template', component: StepTemplate },
    { title: 'Details', component: DetailsComponent },
    { title: 'Review', component: StepPublish },
  ];

  const CurrentStep = steps[step - 1].component;

  // Per-step / per-field / whole-run timing for friction analysis (GA4 behaviour events).
  useWizardTiming(step, steps[step - 1].title);

  // The furthest step the wizard's own data justifies — a user cannot jump to
  // Details without a template, or to Template without an occasion. This gates
  // both the step-indicator dots (rendered from `data`, updated each render)
  // and `goToStep` (reads `dataRef.current` — see its declaration above for
  // why render-time `data` isn't current enough there).
  const maxReachableStep = !data.occasionType ? 1 : !data.templateId ? 2 : steps.length;

  const goToStep = (targetStep: number) => {
    const d = dataRef.current;
    const currentMaxReachableStep = !d.occasionType ? 1 : !d.templateId ? 2 : steps.length;
    if (targetStep > currentMaxReachableStep) return;
    setStep(targetStep);
    setTimeout(() => {
      void saveNow();
    }, 50);
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <header className="bg-surface/80 backdrop-blur-md sticky top-0 border-b border-outline-variant/30 flex items-center justify-between px-8 py-4 shadow-sm h-20 z-50">
        <div className="flex items-center gap-6">
          <button
            onClick={() => {
              if (step > 1) {
                goToStep(step - 1);
              } else {
                navigate('/dashboard');
              }
            }}
            className="text-on-surface-variant hover:bg-surface-low rounded-2xl p-3 transition-all hover:scale-105 active:scale-95"
          >
            <Icons.ChevronLeft size={24} />
          </button>
          <BrandLogo variant="full" size="md" />
        </div>
        <div className="flex items-center gap-6">
          <DraftSaveIndicator status={saveStatus} />
          <div className="hidden md:flex flex-col items-end">
            <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant opacity-50">Current Progress</span>
            <span className="text-sm font-bold text-on-surface">Step {step}: {steps[step - 1].title}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center py-12 px-6 lg:px-8 w-full max-w-7xl mx-auto overflow-x-hidden">
        {/* Progress bar */}
        <div className="w-full max-w-4xl mb-20 px-8">
          <div className="relative flex items-center justify-between">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1.5 bg-surface-low rounded-full z-0" />
            <motion.div
              initial={false}
              animate={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
              className="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 bg-primary rounded-full z-10"
              transition={{ type: 'spring', stiffness: 100, damping: 20 }}
            />
            {steps.map((s, idx) => {
              const stepNum = idx + 1;
              const isCompleted = step > stepNum;
              const isCurrent = step === stepNum;
              const isReachable = stepNum <= maxReachableStep;
              return (
                <button
                  key={s.title}
                  onClick={() => goToStep(stepNum)}
                  disabled={!isReachable}
                  aria-disabled={!isReachable}
                  className={cn(
                    'relative z-20 flex flex-col items-center gap-3 focus:outline-none group',
                    isReachable ? 'cursor-pointer' : 'cursor-not-allowed',
                  )}
                >
                  <motion.div
                    animate={isCurrent ? { scale: 1.15 } : { scale: 1 }}
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center border-4 transition-all duration-300 shadow-lg',
                      isCompleted ? 'bg-primary border-primary text-white' :
                      isCurrent ? 'bg-surface-lowest border-primary text-primary ring-4 ring-primary/10' :
                      isReachable ? 'bg-surface-lowest border-surface-low text-on-surface-variant group-hover:border-primary/50' :
                      'bg-surface-lowest border-surface-low text-on-surface-variant/40',
                    )}
                  >
                    {isCompleted ? <Icons.CheckCircle size={20} /> : <span className="text-sm font-black">{stepNum}</span>}
                  </motion.div>
                  <span className={cn(
                    'text-[10px] font-black uppercase tracking-widest transition-colors duration-300',
                    isCurrent ? 'text-primary opacity-100' : isReachable ? 'text-on-surface-variant opacity-40 group-hover:opacity-100' : 'text-on-surface-variant opacity-20',
                  )}>
                    {s.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {hydrating ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            <p className="text-on-surface-variant font-medium">Loading invitation details...</p>
          </div>
        ) : (
          /* mode="wait" holds the next step unmounted until the outgoing one's exit
             animation completes. A spring transition has no fixed duration — it settles
             asymptotically, and framer-motion decides "done" by a velocity/position
             threshold. Under any main-thread load (e.g. the Template step loading several
             preview videos/images while it exits), that threshold can take an unpredictable,
             sometimes multi-second, amount of time to cross — the step indicator in the
             header advances immediately (it's a separate, unrelated animation) while the
             content pane appears to hang. A tween has a fixed duration and always
             completes on schedule regardless of main-thread load, which removes the
             unpredictability. See docs/features/type-a-engine/HANDOFF.md for the
             2026-07-26 report of this as an intermittent wizard hang. */
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
              className="w-full flex justify-center"
            >
              <CurrentStep
                onNext={() => goToStep(Math.min(step + 1, steps.length))}
                onBack={() => goToStep(Math.max(step - 1, 1))}
                data={data}
                updateData={updateData}
                draftId={draftId}
                saveDraftNow={saveNow}
              />
            </motion.div>
          </AnimatePresence>
        )}
      </main>
    </div>
  );
}
